import type { GrWidgetLoaderVariant } from '@components/loaders';

import {
  DASHBOARD_CARD_ITEMS_MAX,
  type ChartBuilderChartFamily,
  type DashboardCardItem,
  type DashboardCardLayout,
  type DashboardChartType,
} from '../types';

const VALID_CARD_LAYOUTS: ReadonlyArray<DashboardCardLayout> = [
  'auto',
  'horizontal',
  'vertical',
  'primary-stack',
];

export function makeItemId(): string {
  const rand = Math.random().toString(36).slice(2, 8);
  return `item-${Date.now().toString(36)}-${rand}`;
}

export function clampCardItems(
  items: ReadonlyArray<DashboardCardItem>,
): ReadonlyArray<DashboardCardItem> {
  return items.slice(0, DASHBOARD_CARD_ITEMS_MAX);
}

export function resolveAutoLayout(_itemCount: number): Exclude<DashboardCardLayout, 'auto'> {
  return 'horizontal';
}

export function resolveCardLayout(
  layout: DashboardCardLayout | undefined,
  itemCount: number,
): Exclude<DashboardCardLayout, 'auto'> {
  if (!layout || layout === 'auto') return resolveAutoLayout(itemCount);
  return layout;
}

export function isValidCardLayout(value: unknown): value is DashboardCardLayout {
  return typeof value === 'string' && VALID_CARD_LAYOUTS.includes(value as DashboardCardLayout);
}

export function chartTypeForChartFamily(family: ChartBuilderChartFamily | undefined): DashboardChartType {
  switch (family) {
    case 'bar-horizontal': return 'bar-horizontal';
    case 'area':            return 'area';
    case 'donut':           return 'donut';
    default:                return 'bar';
  }
}

/**
 * A widget's loading placeholder should be the silhouette of what replaces it,
 * so the reveal is a fill-in rather than a swap. Chart types that render through
 * the same visual family share a silhouette.
 */
export function widgetLoaderVariantForChartType(type: DashboardChartType): GrWidgetLoaderVariant {
  switch (type) {
    case 'line':
    case 'area':
      return 'line';
    case 'bar':
    case 'stacked-bar':
      return 'bar';
    case 'bar-horizontal':
    case 'rating-bars':
    case 'source-list':
      return 'bar-horizontal';
    case 'donut':
      return 'donut';
  }
}

export function ensureItemIds(
  items: ReadonlyArray<DashboardCardItem>,
): ReadonlyArray<DashboardCardItem> {
  const seen = new Set<string>();
  return items.map((it) => {
    let id = it.id;
    if (!id || seen.has(id)) id = makeItemId();
    seen.add(id);
    return it.id === id ? it : ({ ...it, id } as DashboardCardItem);
  });
}
